import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {ReactNode} from 'react'
import Icon from '../components/features/Icon'
import InteractivePhone3D from '../components/InteractivePhone3D'
import {useAuth} from '../auth/useAuth'
import './FeaturesPage.css'

type StageKind = 'rotate' | 'csg' | 'scenes' | 'privacy' | 'carousel' | 'overlays'

interface Differentiator {
  tag: string
  title: ReactNode
  body: string
  bullets: string[]
  tagline?: string
  stage: StageKind
  reverse?: boolean
}

const DIFFERENTIATORS: Differentiator[] = [
  {
    tag: 'Real 3D, not 2D compositing',
    title: (
      <>
        True <em>three-dimensional</em> mockups.
      </>
    ),
    body: 'Most tools paste your screenshot onto a flat photo. Ecranify renders a real 3D scene — rotate the device under any angle, change lighting, materials, and camera perspective in real time.',
    bullets: [
      'Free orbit camera + presets',
      'Real-time material & lighting updates',
      'Physically-based shading, no canned poses',
    ],
    tagline: 'Photorealistic. Yours to direct.',
    stage: 'rotate',
  },
  {
    tag: 'CSG device geometry',
    title: (
      <>
        Every device is <em>parametric</em>, not a Photoshop mask.
      </>
    ),
    body: 'Device shapes are computed with Constructive Solid Geometry in a Web Worker. Tweak corner radius, camera cutout, button placement, speakers, antennas — every detail is editable, not baked into a photo. Want a square phone? A super-thin slab? A chunky brick with three home buttons and two charging ports? A 14-camera concept array? Go wild — every shape is beautiful in its own way.',
    bullets: [
      'Adjust radii, cutouts, buttons, ports',
      'Live ghost preview while CSG re-bakes',
      'Save your weird future-phone as a reusable preset',
    ],
    tagline: 'No two screens have to look alike.',
    stage: 'csg',
    reverse: true,
  },
  {
    tag: 'Multi-scene projects',
    title: (
      <>
        One project. <em>Multiple scenes</em>. One ZIP.
      </>
    ),
    body: 'Build a full App Store / Google Play set in a single project — intro, feature 1, feature 2, social proof, CTA. Hit export once and download the whole launch in store-ready resolutions.',
    bullets: [
      'Reusable scene templates per project',
      'Batch render every scene at once',
      'Re-download paid renders anytime — free',
    ],
    tagline: 'Ship the whole listing in an afternoon.',
    stage: 'scenes',
  },
  {
    tag: 'Privacy + fork economy',
    title: (
      <>
        Share the design. <em>Keep your screens private</em>. Earn while others ship.
      </>
    ),
    body: 'Publish a project to the gallery and your custom screen content stays in your account. When someone forks it and exports, you earn a credit — every time. A growth loop the screenshot-pasting crowd can’t copy.',
    bullets: [
      'Forks render with separate public thumbnails — your live screen stays invisible',
      'Render premium scenes for free with earned credits',
      'Crown badge for premium projects that lift your gallery rank',
    ],
    tagline: 'Your unreleased UI stays unreleased — and pays you back.',
    stage: 'privacy',
    reverse: true,
  },
  {
    tag: 'Carousel export',
    title: (
      <>
        One <em>wide composition</em>, sliced into store-ready carousels.
      </>
    ),
    body: 'Flip Carousel on a scene and Ecranify renders a single wide composition that spans 2–5 frames — perfect for visual stories that read left-to-right across your App Store gallery. The phone, the typography, the background — all one continuous canvas. We slice it; you ship it.',
    bullets: [
      '2–5 frames per scene from a single composition',
      'Each slice exports at full per-frame resolution',
      'Download each frame separately, or the whole carousel as a ZIP',
    ],
    tagline: 'Tell a story across the gallery, not inside one screen.',
    stage: 'carousel',
  },
  {
    tag: 'Text & sprite overlays',
    title: (
      <>
        Captions and stickers — <em>natively in your 3D scene</em>.
      </>
    ),
    body: 'Drop headlines, logos, and PNG stickers anywhere on the scene. 2D variants sit in screen-space and follow your export frame exactly — great for crisp callouts. 3D variants live in world space alongside the device: lit by the same lights, projected through the same camera, casting real shadows on the same ground. Pick your font or upload your image; the editor handles the math.',
    bullets: [
      '2D text + 2D sprites — screen-space overlays that survive export to PNG',
      '3D text + 3D sprite planes — lit, shadowed, perspective-correct, with PBR materials',
      'PNG alpha preserved; metalness, roughness, clearcoat & unlit toggle on 3D sprites',
    ],
    tagline: 'Annotate without leaving the editor.',
    stage: 'overlays',
    reverse: true,
  },
]

interface CapabilityKey {
  key: 'undo' | 'text' | 'lighting' | 'backgrounds' | 'export' | 'mobile'
  icon: 'undo' | 'type' | 'sun' | 'image' | 'layers' | 'phone'
}

const CAPABILITIES: CapabilityKey[] = [
  {key: 'undo', icon: 'undo'},
  {key: 'text', icon: 'type'},
  {key: 'lighting', icon: 'sun'},
  {key: 'backgrounds', icon: 'image'},
  {key: 'export', icon: 'layers'},
  {key: 'mobile', icon: 'phone'},
]

const RotateStage = () => (
  <div className="m-stage">
    <div className="m-stage-grid" />
    <div className="stage-rotate">
      <svg className="rotate-axes" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          d="M 50 8 A 42 42 0 0 1 92 50"
          stroke="rgba(124, 92, 255, 0.45)"
          strokeWidth="0.4"
          strokeDasharray="2 2"
          fill="none"
        />
        <path
          d="M 8 50 A 42 42 0 0 1 50 8"
          stroke="rgba(255, 122, 184, 0.4)"
          strokeWidth="0.4"
          strokeDasharray="2 2"
          fill="none"
        />
      </svg>
      <InteractivePhone3D className="rotate-phone3d" />
    </div>
  </div>
)

const CsgStage = () => (
  <div className="m-stage">
    <div className="m-stage-grid" />
    <div className="stage-csg">
      <div className="csg-shape">
        <svg viewBox="0 0 280 200" className="csg-svg">
          <defs>
            <linearGradient id="csgGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(124, 92, 255, 0.5)" />
              <stop offset="100%" stopColor="rgba(255, 122, 184, 0.4)" />
            </linearGradient>
          </defs>
          <rect x="40" y="20" width="200" height="160" rx="28" fill="none" stroke="url(#csgGrad)" strokeWidth="1.6" />
          <rect
            x="48"
            y="28"
            width="184"
            height="144"
            rx="22"
            fill="none"
            stroke="rgba(196, 181, 253, 0.35)"
            strokeWidth="0.8"
            strokeDasharray="3 3"
          />
          <circle cx="140" cy="32" r="4" fill="none" stroke="rgba(255, 200, 80, 0.7)" strokeWidth="1.2" />
          <line x1="40" y1="60" x2="36" y2="60" stroke="rgba(196, 181, 253, 0.5)" strokeWidth="1" />
          <line x1="40" y1="80" x2="36" y2="80" stroke="rgba(196, 181, 253, 0.5)" strokeWidth="1" />
          <line x1="240" y1="100" x2="244" y2="100" stroke="rgba(196, 181, 253, 0.5)" strokeWidth="1" />
        </svg>
        <div className="csg-handles">
          <div className="csg-handle csg-handle-tl" />
          <div className="csg-handle csg-handle-tr" />
          <div className="csg-handle csg-handle-bl" />
          <div className="csg-handle csg-handle-br" />
          <div className="csg-label csg-label-top">radius 28</div>
          <div className="csg-label csg-label-right">button 2.4</div>
        </div>
      </div>
    </div>
  </div>
)

const ScenesStage = () => (
  <div className="m-stage">
    <div className="m-stage-grid" />
    <div className="stage-scenes">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`scene-tile scene-tile-${i}`}>
          <span className="scene-num">Scene {i}</span>
        </div>
      ))}
    </div>
  </div>
)

const PrivacyStage = () => {
  const {t} = useTranslation()
  return (
    <div className="m-stage">
      <div className="m-stage-grid" />
      <div className="stage-privacy-fork">
        <div className="pf-row">
          <div className="pf-col">
            <span className="pf-label pf-label-public">{t('features.privacy.viewersSee')}</span>
            <div className="privacy-screen private" />
          </div>
          <div className="pf-arrow" aria-hidden>
            <Icon name="arrow" size={16} />
          </div>
          <div className="pf-col">
            <span className="pf-label pf-label-private">{t('features.privacy.youKeep')}</span>
            <div className="privacy-screen" />
          </div>
        </div>
        <div className="pf-fork-row">
          <div className="pf-fork-node">
            <div className="pf-fork-icon pf-fork-icon-publish">
              <Icon name="image" size={13} />
            </div>
            {t('features.privacy.publish')}
          </div>
          <span className="pf-fork-line" />
          <div className="pf-fork-node">
            <div className="pf-fork-icon pf-fork-icon-fork">
              <Icon name="fork" size={13} />
            </div>
            {t('features.privacy.fork')}
          </div>
          <span className="pf-fork-line" />
          <div className="pf-fork-node">
            <div className="pf-fork-icon pf-fork-icon-earn">
              <Icon name="coin" size={13} />
            </div>
            {t('features.privacy.earn')}
          </div>
        </div>
      </div>
    </div>
  )
}

// Carousel stage — wide composition + dividers up top, three sliced frames
// below. Mirrors the in-editor frame overlay (dashed dividers, slice numbers)
// so the visual is consistent with what users see when they enable Carousel.
const CarouselStage = () => (
  <div className="m-stage">
    <div className="m-stage-grid" />
    <div className="stage-carousel">
      <div className="cs-wide" aria-hidden>
        <span className="cs-wide-tag">1 wide render · 3 × 1290 × 2796</span>
        <span className="cs-divider cs-divider-1" />
        <span className="cs-divider cs-divider-2" />
      </div>
      <div className="cs-arrow" aria-hidden>
        <Icon name="arrow" size={14} />
      </div>
      <div className="cs-frames">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`cs-frame cs-frame-${i}`}>
            <span className="cs-frame-num">Frame {i}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

// Overlays stage — a device-ish frame with a floating 3D-text headline
// behind/above and a sprite "logo" pinned to its side. Both overlays carry
// soft drop shadows hinting at real depth, mirroring how the editor actually
// places text/sprites in the scene rather than on top of a flat composite.
const OverlaysStage = () => (
  <div className="m-stage">
    <div className="m-stage-grid" />
    <div className="stage-overlays">
      <div className="ov-text ov-text-3d" aria-hidden>
        Your Headline
      </div>
      <div className="ov-device" aria-hidden>
        <div className="ov-device-screen" />
        <span className="ov-text ov-text-2d">Tap to start</span>
      </div>
      <div className="ov-sprite ov-sprite-3d" aria-hidden>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
          <path d="M4 7l8-4 8 4-8 4-8-4z" stroke="rgba(255,255,255,0.95)" strokeWidth="1.4" strokeLinejoin="round" />
          <path
            d="M4 12l8 4 8-4M4 17l8 4 8-4"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="ov-sprite ov-sprite-2d" aria-hidden>
        NEW
      </div>
    </div>
  </div>
)

const Stage = ({kind}: {kind: StageKind}) => {
  switch (kind) {
    case 'rotate':
      return <RotateStage />
    case 'csg':
      return <CsgStage />
    case 'scenes':
      return <ScenesStage />
    case 'privacy':
      return <PrivacyStage />
    case 'carousel':
      return <CarouselStage />
    case 'overlays':
      return <OverlaysStage />
    default:
      return null
  }
}

const FeaturesPage = () => {
  const {t} = useTranslation()
  const {user} = useAuth()
  const startBuildingHref = user ? '/editor/new' : '/free'

  return (
    <div className="features-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="features-page">
        <header className="feat-hero">
          <span className="feat-eyebrow">{t('features.eyebrow')}</span>
          <h1 className="feat-title">
            {t('features.titlePrefix')} <em>{t('features.titleEm')}</em>
            {t('features.titleSuffix')}
          </h1>
          <p className="feat-sub">{t('features.subtitle')}</p>
        </header>

        {DIFFERENTIATORS.map((d, i) => (
          <section key={d.tag} className={`marquee${d.reverse ? ' reverse' : ''}`}>
            <div className="m-copy">
              <div className="m-tag">
                <span className="m-tag-num">{String(i + 1).padStart(2, '0')}</span>
                {d.tag}
              </div>
              <h2 className="m-title">{d.title}</h2>
              <p className="m-body">{d.body}</p>
              <ul className="m-bullets">
                {d.bullets.map((b) => (
                  <li key={b} className="m-bullet">
                    <span className="m-bullet-icon">
                      <Icon name="check" size={13} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {d.tagline && (
                <div className="m-tagline">
                  <span className="m-tagline-prefix">{t('features.diff.tagline')}</span>
                  {d.tagline}
                </div>
              )}
            </div>
            <Stage kind={d.stage} />
          </section>
        ))}

        <section className="cap-section">
          <div className="cap-head">
            <span className="cap-eyebrow">{t('features.capsEyebrow')}</span>
            <h2 className="cap-h2">
              {t('features.capsTitlePrefix')} <em>{t('features.capsTitleEm')}</em>
              {t('features.capsTitleSuffix')}
            </h2>
            <p className="cap-sub">{t('features.capsSubtitle')}</p>
          </div>
          <div className="cap-grid">
            {CAPABILITIES.map((c) => (
              <div key={c.key} className="cap-card">
                <div className="cap-card-icon">
                  <Icon name={c.icon} size={18} />
                </div>
                <div className="cap-card-title">{t(`features.capabilities.${c.key}.title`)}</div>
                <div className="cap-card-body">{t(`features.capabilities.${c.key}.body`)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="feat-cta">
          <h2>{t('features.cta.title')}</h2>
          <p>{t('features.cta.body')}</p>
          <div className="feat-cta-row">
            <Link to={startBuildingHref} className="feat-cta-primary">
              <span>{t('features.cta.primary')}</span>
              <Icon name="arrow" size={16} />
            </Link>
            <Link to="/projects" className="feat-cta-secondary">
              {t('features.cta.secondary')}
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}

export default FeaturesPage
